/**
 * PublishTargetsView — per-publication distribution targets.
 *
 * S-066 / FEAT-006 (Phase 6). Replaces the AdminStubs.PublishTargetsView
 * placeholder. Admin-only screen reachable from Settings → Publish targets.
 *
 * What it does:
 *   - Workspace selector (top) — calls useActiveWorkspace().activate()
 *     on change so cross-workspace mutations land under the right RLS
 *     scope (same pattern as PublicationsView / TeamView).
 *   - Publication selector (second row) — local state, lists the
 *     publications in the active workspace via
 *     GET /publications?agencyclient_id=N. Resets to the first
 *     publication of the new workspace whenever the workspace flips.
 *   - Lists publish targets for the selected publication via
 *     GET /publish-targets?agencyclient_id=N. We filter client-side by
 *     publication_id because the API only exposes the agencyclient_id
 *     query filter today; the response payload is small (~5 rows per
 *     publication in realistic deploys) so the filter cost is trivial.
 *   - Table columns: Type, Name, Slug, Status (enabled/disabled),
 *     Credentials (mask), Created, Actions (Edit / Delete).
 *   - Click row OR Edit → drawer with editable fields:
 *       type (dropdown, enum from TARGET_TYPES)
 *       name, slug
 *       site_url
 *       enabled toggle
 *       credentials JSON (textarea, optional — if blank, server keeps
 *         existing ciphertext; if non-blank, validated as JSON
 *         client-side and re-encrypted server-side per ADR-002).
 *   - Create modal: same fields as Edit, credentials field non-empty
 *     for the typical case (Substack/WordPress flows always have a
 *     token to capture).
 *
 * Credentials handling (ADR-002 ciphertext + masking):
 *   - The API returns `credentials_ciphertext` as a masked string
 *     (e.g. "***" hint) for admins per maskCiphertext(), and null for
 *     non-admins. We never see plaintext after save.
 *   - On Edit, the credentials textarea starts EMPTY (we don't echo
 *     a fake placeholder that could be mis-saved). A hint copy reads
 *     "Leave blank to keep existing credentials" — typical for slug/
 *     name-only updates.
 *   - On Create, credentials is optional in the API schema but most
 *     real targets need it; the modal surfaces a type-aware hint.
 *
 * Behaviors:
 *   - Client-side JSON validation on the credentials textarea: try
 *     JSON.parse before submit; inline error if invalid. The API also
 *     enforces an 8000-char ceiling per S-076.
 *   - 409 slug_taken surfaces inline on the slug field.
 *   - 422 validation errors surface with the server message verbatim.
 *
 * Edge cases handled:
 *   - No workspaces — render the same "no workspaces" copy as
 *     PublicationsView (PublishTargets requires a publication, which
 *     requires a workspace).
 *   - No publications in the active workspace — empty state pointing
 *     to /settings/publications to create one.
 *   - No targets for the active publication — empty state with the
 *     "+ New target" CTA.
 *   - Workspace switch mid-flow — publication selector resets and the
 *     targets list refetches under the new scope.
 *
 * RBAC:
 *   - Route reachable only via the admin-gated Settings rail.
 *   - The API enforces requireAdmin on POST/PATCH/DELETE; non-admins
 *     would see 403 on every mutation (handled via ApiError display).
 */

import { useEffect, useMemo, useState } from "react";
// Consolidation (option 3): shared admin modal/drawer shell. Replaces the
// local ModalShell/DrawerShell that used to live at the bottom of this file.
import { ModalShell, DrawerShell } from "@/components/SettingsModalShell";
import { SectionHeader } from "@/components/SectionHeader";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useApiMutation, useApiQuery } from "@/lib/api";
import { useActiveWorkspace, useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";

// Mirrors apps/api/src/routes/publish-targets.ts TARGET_TYPES. kit (S-124)
// + docx/html_file (S-128) now carry typed credential forms (FEAT-011
// Groups 2+3).
const TARGET_TYPES = [
  "substack",
  "wordpress",
  "static_site",
  "email_gmail",
  "linkedin_newsletter",
  "webhook",
  "kit",
  "docx",
  "html_file",
  // FEAT-055 / S-307
  "mailchimp",
] as const;
type TargetType = (typeof TARGET_TYPES)[number];

const TARGET_TYPE_LABEL: Record<TargetType, string> = {
  substack: "Substack",
  wordpress: "WordPress",
  static_site: "Static site",
  email_gmail: "Gmail",
  linkedin_newsletter: "LinkedIn Newsletter",
  webhook: "Webhook",
  kit: "Kit",
  docx: "Word document (.docx)",
  html_file: "HTML file",
  mailchimp: "Mailchimp",
};

// Per-type hint for the fallback raw-JSON textarea (used when the target
// type has no typed-input UI yet). Substack and WordPress have dedicated
// fields below — this hint is only shown for the raw-JSON fallback.
const TARGET_TYPE_CREDENTIALS_HINT: Record<TargetType, string> = {
  substack:
    'JSON with your Substack session cookie. Example: {"session_cookie":"substack.sid=..."}',
  wordpress:
    'JSON with username + application password. Example: {"username":"editor","app_password":"xxxx xxxx xxxx xxxx"}',
  static_site:
    'Credentials JSON for this target type. (Typed inputs not yet available for this target.)',
  email_gmail:
    'Credentials JSON for this target type. (Typed inputs not yet available for this target.)',
  linkedin_newsletter:
    'Credentials JSON for this target type. (Typed inputs not yet available for this target.)',
  webhook:
    'JSON with the receiver URL + shared HMAC secret. Example: {"url":"https://your-worker.workers.dev/publish","secret":"...","hmac_header":"X-CCM-Signature"}',
  kit:
    'JSON with your Kit (kit.com) API key + sender. Example: {"api_key":"kit_...","from_email":"team@example.com","from_name":"Your Name"}',
  docx:
    'JSON with the upload target. Example: {"upload_target":"platform_r2"} or a tenant_r2 block with R2 credentials.',
  html_file:
    'JSON with the upload target + image_mode. Example: {"upload_target":"platform_r2","image_mode":"embedded","inline_css":true}',
  mailchimp:
    'JSON with your Mailchimp API key + sender. Example: {"api_key":"abc123...-us21","from_email":"team@example.com","from_name":"Your Name"}',
};

// S-078: target types that get typed credential inputs. Other types fall
// through to the raw-JSON textarea (operator escape hatch) so the modal
// doesn't break as new target types are added later.
const TYPED_CRED_TYPES: ReadonlySet<TargetType> = new Set([
  "substack",
  "wordpress",
  "webhook",
  "kit",
  "docx",
  "html_file",
  "mailchimp",
]);

// File-output target types share an upload-credentials form (S-128).
const FILE_OUTPUT_TYPES: ReadonlySet<TargetType> = new Set(["docx", "html_file"]);

// Validation thresholds for typed fields. Sanity-only; the server's
// 8000-char ceiling on the final JSON blob still applies.
const SUBSTACK_COOKIE_MIN = 10;
const WP_USERNAME_MIN = 1;
const WP_USERNAME_MAX = 120;
const WP_APP_PASSWORD_MIN = 20;
const WEBHOOK_SECRET_MIN = 8;
const KIT_API_KEY_MIN = 20; // Kit v4 keys are 40+ chars; 20 catches paste-truncation early
const MAILCHIMP_API_KEY_MIN = 20; // Mailchimp keys are 32-hex-chars + "-" + dc suffix

interface Agencyclient {
  id: number;
  name: string;
  slug: string;
}

interface Publication {
  id: number;
  name: string;
  slug: string;
  agencyclientId?: number;
  agencyclient_id?: number;
}

interface PublishTarget {
  id: number;
  tenantId?: number;
  agencyclientId?: number;
  agencyclient_id?: number;
  publicationId?: number;
  publication_id?: number;
  slug: string;
  type: TargetType;
  name: string;
  siteUrl?: string | null;
  site_url?: string | null;
  // Server masks ciphertext: returns "***" for admins, null for non-admins.
  credentials_ciphertext?: string | null;
  // BUG-038 — the currently-configured ESP audience id (Kit/Mailchimp
  // only), decrypted server-side. Not secret on its own — safe to show so
  // the edit drawer can confirm a save actually took, since every
  // credentials field otherwise starts blank on open.
  configured_audience_id?: string | null;
  defaultSettings?: Record<string, unknown>;
  default_settings?: Record<string, unknown>;
  contentTypeOverrides?: Record<string, unknown>;
  content_type_overrides?: Record<string, unknown>;
  enabled: boolean;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
}

interface PublishTargetsResponse {
  data: PublishTarget[];
  meta: { total: number; limit: number; offset: number };
}

interface PublicationsResponse {
  data: Publication[];
  meta: { total: number; limit: number; offset: number };
}

const PT_WS_KEY = "ccm:publish-targets:active_ws";
const PT_PUB_KEY = "ccm:publish-targets:active_pub";

// ---------- Helpers ----------

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function isValidSlug(s: string): boolean {
  if (s.length < 1 || s.length > 80) return false;
  return /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(s);
}

function fmtDate(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function pubIdOf(t: PublishTarget): number | null {
  return t.publicationId ?? t.publication_id ?? null;
}

function siteUrlOf(t: PublishTarget): string | null {
  return t.siteUrl ?? t.site_url ?? null;
}

function createdAtOf(t: PublishTarget): string | undefined {
  return t.createdAt ?? t.created_at;
}

// ---------- Credentials section (S-078) ----------

/**
 * Per-target-type typed inputs + raw-JSON fallback for the credentials
 * field. Used in both the Create modal and the Edit drawer.
 *
 * Contract:
 *   - The server stores credentials as a JSON string (encrypted at rest)
 *     and accepts the field as `credentials: string` (POST/PATCH body).
 *   - This component owns the typed UI for substack + wordpress and
 *     surfaces the raw-JSON textarea otherwise (or under an Advanced
 *     disclosure for the operator escape hatch).
 *   - The parent component reads the packaged JSON string via
 *     `state.toJson()` at submit time; if that returns `null`, no
 *     credentials field should be sent (e.g. edit-drawer "leave blank to
 *     keep existing").
 *   - In edit mode (`mode === "edit"`), all fields start blank and the
 *     helper copy reads "Leave blank to keep existing credentials. Fill
 *     in to replace them." Submit only includes credentials if at least
 *     one field has a value.
 */
interface CredentialsState {
  // Substack typed fields
  substackCookie: string;
  substackPublicationUrl: string;
  // WordPress typed fields
  wpSiteUrl: string;
  wpUsername: string;
  wpAppPassword: string;
  // Webhook typed fields (FEAT-011 / S-182)
  webhookUrl: string;
  webhookSecret: string;
  webhookHmacHeader: string;
  // Kit typed fields (FEAT-011 / S-124)
  kitApiKey: string;
  kitFromEmail: string;
  kitFromName: string;
  // FEAT-055 / S-306 — renamed from kitSegmentId now that it's populated by
  // a real audience picker, not just manual entry. kitAudienceName is
  // display-only (Kit's credentials schema has no name field to send).
  kitAudienceId: string;
  kitAudienceName: string;
  kitSubjectPrefix: string;
  // Mailchimp typed fields (FEAT-055 / S-307)
  mailchimpApiKey: string;
  mailchimpFromEmail: string;
  mailchimpFromName: string;
  mailchimpAudienceId: string;
  mailchimpAudienceName: string;
  // File-output typed fields (FEAT-011 / S-128) — shared by docx + html_file.
  fileUploadTarget: "platform_r2" | "tenant_r2";
  fileFilenameTemplate: string;
  fileTenantAccountId: string;
  fileTenantAccessKeyId: string;
  fileTenantSecretAccessKey: string;
  fileTenantBucket: string;
  fileTenantPublicUrlBase: string;
  // html_file-only extras
  htmlFileImageMode: "embedded" | "separate";
  htmlFileInlineCss: boolean;
  // Operator escape hatch — raw JSON overrides typed fields when non-empty
  rawJson: string;
  advancedOpen: boolean;
}

function emptyCredentialsState(): CredentialsState {
  return {
    substackCookie: "",
    substackPublicationUrl: "",
    wpSiteUrl: "",
    wpUsername: "",
    wpAppPassword: "",
    webhookUrl: "",
    webhookSecret: "",
    webhookHmacHeader: "",
    kitApiKey: "",
    kitFromEmail: "",
    kitFromName: "",
    kitAudienceId: "",
    kitAudienceName: "",
    kitSubjectPrefix: "",
    mailchimpApiKey: "",
    mailchimpFromEmail: "",
    mailchimpFromName: "",
    mailchimpAudienceId: "",
    mailchimpAudienceName: "",
    fileUploadTarget: "platform_r2",
    fileFilenameTemplate: "",
    fileTenantAccountId: "",
    fileTenantAccessKeyId: "",
    fileTenantSecretAccessKey: "",
    fileTenantBucket: "",
    fileTenantPublicUrlBase: "",
    htmlFileImageMode: "embedded",
    htmlFileInlineCss: false,
    rawJson: "",
    advancedOpen: false,
  };
}

interface CredentialsValidation {
  // Inline error to render under the relevant field. null = ok.
  substackCookieError: string | null;
  substackPublicationUrlError?: string | null;
  wpSiteUrlError?: string | null;
  wpUsernameError: string | null;
  wpAppPasswordError: string | null;
  // Webhook field errors (optional — older return shapes omit them).
  webhookUrlError?: string | null;
  webhookSecretError?: string | null;
  // Kit field errors (S-124).
  kitApiKeyError?: string | null;
  kitFromEmailError?: string | null;
  kitFromNameError?: string | null;
  // Mailchimp field errors (FEAT-055 / S-307).
  mailchimpApiKeyError?: string | null;
  mailchimpFromEmailError?: string | null;
  mailchimpFromNameError?: string | null;
  // File-output field errors (S-128).
  fileTenantError?: string | null;
  rawJsonError: string | null;
  // True if the parent's submit button should be disabled.
  blockSubmit: boolean;
  // Final JSON string to send as `credentials`, or null if no creds set.
  // In edit mode, null means "keep existing ciphertext".
  toJson: string | null;
  // BUG-037 — set only when the ESP audience picker was used and nothing
  // else on the form was touched: sent as `credentials_patch` instead of
  // `credentials` so the server merges just this field into the existing
  // stored credentials, without needing the rest of the blob (e.g. the API
  // key, which edit mode never has — it's not echoed back). Mutually
  // exclusive with `toJson` being non-null.
  credentialsPatch?: Record<string, string> | null;
}

/**
 * Validate the credentials state for a given target type and mode.
 *
 * Rules (S-078):
 *   - Advanced raw JSON wins if non-empty: must parse as JSON, else
 *     `rawJsonError` is set and submit is blocked.
 *   - Substack (typed): when in create mode the session cookie is
 *     required (>=10 chars trimmed). In edit mode it's optional, but
 *     if any value is typed it must still meet the min-length sanity.
 *   - WordPress (typed): username (>=1, <=120 trimmed) + app password
 *     (>=20 chars trimmed; spaces in 24-char "xxxx xxxx ..." count
 *     toward the raw length but we trim before validating).
 *   - Fallback types (static_site, email_gmail, linkedin_newsletter):
 *     no typed validation; the raw-JSON textarea does the work.
 */
function validateCredentials(
  state: CredentialsState,
  type: TargetType,
  mode: "create" | "edit"
): CredentialsValidation {
  const rawTrimmed = state.rawJson.trim();
  const substackTrimmed = state.substackCookie.trim();
  const wpSiteUrlTrimmed = state.wpSiteUrl.trim();
  const wpUsernameTrimmed = state.wpUsername.trim();
  const wpAppPasswordTrimmed = state.wpAppPassword.trim();

  // Advanced raw JSON wins when non-empty.
  if (rawTrimmed.length > 0) {
    if (rawTrimmed.length > 8000) {
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        rawJsonError: "Credentials too long (max 8000 chars).",
        blockSubmit: true,
        toJson: null,
      };
    }
    try {
      // Re-serialize through JSON.parse + JSON.stringify so the server
      // gets canonical JSON (parser would have rejected anyway, but
      // this ensures consistent storage).
      const parsed = JSON.parse(rawTrimmed) as unknown;
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        rawJsonError: null,
        blockSubmit: false,
        toJson: JSON.stringify(parsed),
      };
    } catch {
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        rawJsonError: "Credentials must be valid JSON.",
        blockSubmit: true,
        toJson: null,
      };
    }
  }

  if (type === "substack") {
    const pubUrlTrimmed = state.substackPublicationUrl.trim();
    const anyTouched = substackTrimmed.length > 0 || pubUrlTrimmed.length > 0;
    if (!anyTouched) {
      // Create: required. Edit: optional ("leave blank to keep").
      if (mode === "create") {
        return {
          substackCookieError: null, // don't yell pre-touch; submit will set if needed
          substackPublicationUrlError: null,
          wpUsernameError: null,
          wpAppPasswordError: null,
          rawJsonError: null,
          blockSubmit: true,
          toJson: null,
        };
      }
      // Edit + both empty → keep existing
      return {
        substackCookieError: null,
        substackPublicationUrlError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        rawJsonError: null,
        blockSubmit: false,
        toJson: null,
      };
    }
    // One or both fields filled — validate both.
    const cookieError =
      substackTrimmed.length > 0 && substackTrimmed.length < SUBSTACK_COOKIE_MIN
        ? `Session cookie too short (min ${SUBSTACK_COOKIE_MIN} characters).`
        : substackTrimmed.length === 0 && mode === "create"
          ? "Session cookie is required."
          : null;
    const pubUrlError =
      pubUrlTrimmed.length === 0 && mode === "create"
        ? "Publication URL is required."
        : pubUrlTrimmed.length > 0 && !pubUrlTrimmed.startsWith("https://")
          ? "Publication URL must start with https://"
          : null;
    if (cookieError || pubUrlError) {
      return {
        substackCookieError: cookieError,
        substackPublicationUrlError: pubUrlError,
        wpUsernameError: null,
        wpAppPasswordError: null,
        rawJsonError: null,
        blockSubmit: true,
        toJson: null,
      };
    }
    return {
      substackCookieError: null,
      substackPublicationUrlError: null,
      wpUsernameError: null,
      wpAppPasswordError: null,
      rawJsonError: null,
      blockSubmit: false,
      toJson: JSON.stringify({
        mode: "api",
        cookie: substackTrimmed,
        publication_url: pubUrlTrimmed,
      }),
    };
  }

  if (type === "wordpress") {
    const anyTouched =
      wpSiteUrlTrimmed.length > 0 ||
      wpUsernameTrimmed.length > 0 ||
      wpAppPasswordTrimmed.length > 0;
    if (!anyTouched) {
      if (mode === "create") {
        return {
          substackCookieError: null,
          wpSiteUrlError: null,
          wpUsernameError: null,
          wpAppPasswordError: null,
          rawJsonError: null,
          blockSubmit: true,
          toJson: null,
        };
      }
      // Edit + all empty → keep existing
      return {
        substackCookieError: null,
        wpSiteUrlError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        rawJsonError: null,
        blockSubmit: false,
        toJson: null,
      };
    }
    // If any field is touched, all three are required.
    let siteUrlError: string | null = null;
    let usernameError: string | null = null;
    let appPasswordError: string | null = null;
    if (!isProbablyUrl(wpSiteUrlTrimmed)) {
      siteUrlError = "Must be a valid https:// URL (e.g. https://example.com).";
    }
    if (
      wpUsernameTrimmed.length < WP_USERNAME_MIN ||
      wpUsernameTrimmed.length > WP_USERNAME_MAX
    ) {
      usernameError = `Username must be ${WP_USERNAME_MIN}–${WP_USERNAME_MAX} characters.`;
    }
    if (wpAppPasswordTrimmed.length < WP_APP_PASSWORD_MIN) {
      appPasswordError = `Application password too short (min ${WP_APP_PASSWORD_MIN} characters).`;
    }
    if (siteUrlError || usernameError || appPasswordError) {
      return {
        substackCookieError: null,
        wpSiteUrlError: siteUrlError,
        wpUsernameError: usernameError,
        wpAppPasswordError: appPasswordError,
        rawJsonError: null,
        blockSubmit: true,
        toJson: null,
      };
    }
    return {
      substackCookieError: null,
      wpSiteUrlError: null,
      wpUsernameError: null,
      wpAppPasswordError: null,
      rawJsonError: null,
      blockSubmit: false,
      toJson: JSON.stringify({
        site_url: wpSiteUrlTrimmed,
        username: wpUsernameTrimmed,
        // Preserve spaces in WP app passwords (24-char "xxxx xxxx ..." format)
        // — we trim leading/trailing whitespace only, not internal spaces.
        app_password: wpAppPasswordTrimmed,
      }),
    };
  }

  if (type === "webhook") {
    const urlTrimmed = state.webhookUrl.trim();
    const secretTrimmed = state.webhookSecret.trim();
    const headerTrimmed = state.webhookHmacHeader.trim();
    const anyTouched = urlTrimmed.length > 0 || secretTrimmed.length > 0;

    if (!anyTouched) {
      // Create: required. Edit: empty → keep existing ciphertext.
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        webhookUrlError: null,
        webhookSecretError: null,
        rawJsonError: null,
        blockSubmit: mode === "create",
        toJson: null,
      };
    }

    // Either field touched → both required + validated.
    let urlError: string | null = null;
    let secretError: string | null = null;
    if (!/^https:\/\/.+/i.test(urlTrimmed)) {
      urlError = "URL must be an https:// endpoint.";
    }
    if (secretTrimmed.length < WEBHOOK_SECRET_MIN) {
      secretError = `Shared secret too short (min ${WEBHOOK_SECRET_MIN} characters).`;
    }
    if (urlError || secretError) {
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        webhookUrlError: urlError,
        webhookSecretError: secretError,
        rawJsonError: null,
        blockSubmit: true,
        toJson: null,
      };
    }
    const creds: Record<string, string> = {
      url: urlTrimmed,
      secret: secretTrimmed,
    };
    if (headerTrimmed.length > 0) creds.hmac_header = headerTrimmed;
    return {
      substackCookieError: null,
      wpUsernameError: null,
      wpAppPasswordError: null,
      webhookUrlError: null,
      webhookSecretError: null,
      rawJsonError: null,
      blockSubmit: false,
      toJson: JSON.stringify(creds),
    };
  }

  if (type === "kit") {
    const apiKey = state.kitApiKey.trim();
    const fromEmail = state.kitFromEmail.trim();
    const fromName = state.kitFromName.trim();
    const segmentId = state.kitAudienceId.trim();
    const subjectPrefix = state.kitSubjectPrefix;
    const anyTouched =
      apiKey.length > 0 || fromEmail.length > 0 || fromName.length > 0;

    // BUG-037 — picking an audience via EspAudiencePicker alone (no other
    // field touched) previously left the form non-dirty (nothing to save)
    // and, if forced dirty some other way, demanded api_key/from_email/
    // from_name that edit mode never has. In edit mode, patch just the
    // audience server-side instead of requiring a full credentials replace.
    if (!anyTouched && segmentId.length > 0 && mode === "edit") {
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        rawJsonError: null,
        blockSubmit: false,
        toJson: null,
        credentialsPatch: { subscriber_segment_id: segmentId },
      };
    }

    if (!anyTouched) {
      // Create: required. Edit: empty → keep existing ciphertext.
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        rawJsonError: null,
        blockSubmit: mode === "create",
        toJson: null,
      };
    }

    let apiKeyError: string | null = null;
    let fromEmailError: string | null = null;
    let fromNameError: string | null = null;
    if (apiKey.length < KIT_API_KEY_MIN) {
      apiKeyError = `API key too short (min ${KIT_API_KEY_MIN} characters).`;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(fromEmail)) {
      fromEmailError = "From email must be a valid email address.";
    }
    if (fromName.length === 0) {
      fromNameError = "From name is required.";
    }
    if (apiKeyError || fromEmailError || fromNameError) {
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        kitApiKeyError: apiKeyError,
        kitFromEmailError: fromEmailError,
        kitFromNameError: fromNameError,
        rawJsonError: null,
        blockSubmit: true,
        toJson: null,
      };
    }
    const creds: Record<string, string> = {
      api_key: apiKey,
      from_email: fromEmail,
      from_name: fromName,
    };
    if (segmentId.length > 0) creds.subscriber_segment_id = segmentId;
    if (subjectPrefix.length > 0) creds.default_subject_prefix = subjectPrefix;
    return {
      substackCookieError: null,
      wpUsernameError: null,
      wpAppPasswordError: null,
      kitApiKeyError: null,
      kitFromEmailError: null,
      kitFromNameError: null,
      rawJsonError: null,
      blockSubmit: false,
      toJson: JSON.stringify(creds),
    };
  }

  if (type === "mailchimp") {
    const apiKey = state.mailchimpApiKey.trim();
    const fromEmail = state.mailchimpFromEmail.trim();
    const fromName = state.mailchimpFromName.trim();
    const audienceId = state.mailchimpAudienceId.trim();
    const anyTouched =
      apiKey.length > 0 || fromEmail.length > 0 || fromName.length > 0;

    // BUG-037 — same fix as Kit's block above: picking an audience alone
    // must not require api_key/from_email/from_name, which edit mode
    // never has pre-filled. Patch just the audience server-side instead.
    if (!anyTouched && audienceId.length > 0 && mode === "edit") {
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        rawJsonError: null,
        blockSubmit: false,
        toJson: null,
        credentialsPatch: { audience_id: audienceId },
      };
    }

    if (!anyTouched) {
      // Create: required. Edit: empty → keep existing ciphertext.
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        rawJsonError: null,
        blockSubmit: mode === "create",
        toJson: null,
      };
    }

    let apiKeyError: string | null = null;
    let fromEmailError: string | null = null;
    let fromNameError: string | null = null;
    if (apiKey.length < MAILCHIMP_API_KEY_MIN) {
      apiKeyError = `API key too short (min ${MAILCHIMP_API_KEY_MIN} characters).`;
    } else if (!apiKey.includes("-")) {
      apiKeyError = 'API key must include the datacenter suffix (e.g. "...-us21").';
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(fromEmail)) {
      fromEmailError = "From email must be a valid email address.";
    }
    if (fromName.length === 0) {
      fromNameError = "From name is required.";
    }
    if (apiKeyError || fromEmailError || fromNameError) {
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        mailchimpApiKeyError: apiKeyError,
        mailchimpFromEmailError: fromEmailError,
        mailchimpFromNameError: fromNameError,
        rawJsonError: null,
        blockSubmit: true,
        toJson: null,
      };
    }
    const creds: Record<string, string> = {
      api_key: apiKey,
      from_email: fromEmail,
      from_name: fromName,
    };
    if (audienceId.length > 0) creds.audience_id = audienceId;
    return {
      substackCookieError: null,
      wpUsernameError: null,
      wpAppPasswordError: null,
      mailchimpApiKeyError: null,
      mailchimpFromEmailError: null,
      mailchimpFromNameError: null,
      rawJsonError: null,
      blockSubmit: false,
      toJson: JSON.stringify(creds),
    };
  }

  if (type === "docx" || type === "html_file") {
    // File-output targets always have a valid default (platform_r2), so the
    // form is never "untouched" — submit is allowed even with no edits in
    // create mode. In edit mode we only send credentials when the operator
    // changes something away from defaults OR fills the tenant block.
    const creds: Record<string, unknown> = {
      upload_target: state.fileUploadTarget,
    };
    if (state.fileFilenameTemplate.trim().length > 0) {
      creds.filename_template = state.fileFilenameTemplate.trim();
    }

    let tenantError: string | null = null;
    if (state.fileUploadTarget === "tenant_r2") {
      const accountId = state.fileTenantAccountId.trim();
      const accessKeyId = state.fileTenantAccessKeyId.trim();
      const secretAccessKey = state.fileTenantSecretAccessKey.trim();
      const bucket = state.fileTenantBucket.trim();
      const publicUrlBase = state.fileTenantPublicUrlBase.trim();
      if (!accountId || !accessKeyId || !secretAccessKey || !bucket) {
        tenantError =
          "Tenant R2 requires account ID, access key, secret key, and bucket.";
      } else {
        creds.account_id = accountId;
        creds.access_key_id = accessKeyId;
        creds.secret_access_key = secretAccessKey;
        creds.bucket = bucket;
        if (publicUrlBase.length > 0) creds.public_url_base = publicUrlBase;
      }
    }

    if (type === "html_file") {
      creds.image_mode = state.htmlFileImageMode;
      creds.inline_css = state.htmlFileInlineCss;
    }

    if (tenantError) {
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        fileTenantError: tenantError,
        rawJsonError: null,
        blockSubmit: true,
        toJson: null,
      };
    }

    // In edit mode, only send credentials if the operator actually changed
    // something (tenant_r2 selected, a filename template, or html options).
    // Otherwise return null to keep existing ciphertext.
    const isDefaultPlatform =
      state.fileUploadTarget === "platform_r2" &&
      state.fileFilenameTemplate.trim().length === 0 &&
      (type !== "html_file" ||
        (state.htmlFileImageMode === "embedded" && !state.htmlFileInlineCss));
    if (mode === "edit" && isDefaultPlatform) {
      return {
        substackCookieError: null,
        wpUsernameError: null,
        wpAppPasswordError: null,
        fileTenantError: null,
        rawJsonError: null,
        blockSubmit: false,
        toJson: null,
      };
    }

    return {
      substackCookieError: null,
      wpUsernameError: null,
      wpAppPasswordError: null,
      fileTenantError: null,
      rawJsonError: null,
      blockSubmit: false,
      toJson: JSON.stringify(creds),
    };
  }

  // Fallback types — no typed inputs available; rely on raw-JSON above.
  // If we get here the raw textarea is empty too → no credentials.
  if (mode === "create") {
    // For unknown/fallback target types, credentials are optional at
    // create time (the API also marks `credentials` as optional).
    return {
      substackCookieError: null,
      wpUsernameError: null,
      wpAppPasswordError: null,
      rawJsonError: null,
      blockSubmit: false,
      toJson: null,
    };
  }
  return {
    substackCookieError: null,
    wpUsernameError: null,
    wpAppPasswordError: null,
    rawJsonError: null,
    blockSubmit: false,
    toJson: null,
  };
}

// FEAT-055 / S-306 — real audience discovery, replacing manual ID entry.
// Only usable once credentials are actually saved server-side (the
// endpoint decrypts the row's stored credentials_ciphertext) — so this
// only renders when targetId is available, i.e. edit mode. Create mode
// keeps the plain text fallback below.
interface AudienceOption {
  id: string;
  name: string;
  memberCountApprox?: number;
}

function EspAudiencePicker({
  targetId,
  audienceId,
  audienceName,
  onSelect,
}: {
  targetId: number;
  audienceId: string;
  audienceName: string;
  onSelect: (id: string, name: string) => void;
}) {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audiences, setAudiences] = useState<AudienceOption[] | null>(null);

  async function loadAudiences() {
    setError(null);
    setLoading(true);
    try {
      const result = await api<{ audiences: AudienceOption[] }>(
        `/publish-targets/${targetId}/audiences`,
        { method: "POST" }
      );
      setAudiences(result.audiences);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? (e.message || "Could not validate credentials.")
          : "Could not load audiences."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="settings-field">
      <span className="settings-label">
        Audience{" "}
        <span className="settings-label-hint">
          Save the API key above first, then validate to load real list names
        </span>
      </span>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn-primary"
          disabled={loading}
          onClick={() => void loadAudiences()}
          // FEAT-055 / S-306 — fixed width so swapping to the shorter
          // "Validating…" label doesn't shrink/reflow the button.
          style={{ minWidth: 220 }}
        >
          {loading ? "Validating…" : "Validate & load audiences"}
        </button>
        {audienceName && (
          <span className="settings-label-hint">Selected: {audienceName}</span>
        )}
      </div>
      {error && <span className="settings-field-error">{error}</span>}
      {audiences && (
        <select
          className="settings-select"
          value={audienceId}
          onChange={(e) => {
            const picked = audiences.find((a) => a.id === e.target.value);
            onSelect(e.target.value, picked?.name ?? "");
          }}
        >
          <option value="">Select an audience…</option>
          {audiences.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.memberCountApprox != null ? ` (${a.memberCountApprox})` : ""}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

interface CredentialsSectionProps {
  type: TargetType;
  state: CredentialsState;
  onChange: (next: CredentialsState) => void;
  validation: CredentialsValidation;
  mode: "create" | "edit";
  /** FEAT-055 / S-306 — the saved target's ID, undefined in create mode. */
  targetId?: number;
  /** BUG-038 — the currently-saved ESP audience id, if any (edit mode only). */
  configuredAudienceId?: string | null;
}

function CredentialsSection({
  type,
  state,
  onChange,
  validation,
  mode,
  targetId,
  configuredAudienceId,
}: CredentialsSectionProps) {
  const [revealSubstack, setRevealSubstack] = useState(false);
  const isTyped = TYPED_CRED_TYPES.has(type);

  // Reset reveal toggle when target type changes.
  useEffect(() => {
    setRevealSubstack(false);
  }, [type]);

  const editHint =
    "Leave blank to keep existing credentials. Fill in to replace them.";

  return (
    <div className="publish-targets-creds-section">
      {isTyped && type === "substack" && (
        <div className="settings-field">
          <span className="settings-label">
            Session cookie{" "}
            <span className="settings-label-hint">
              {mode === "edit"
                ? editHint
                : "Required — encrypted server-side."}
            </span>
          </span>
          <div className="publish-targets-creds-hint">
            Paste the full <code>Cookie:</code> header blob from a logged-in
            browser request to substack.com (DevTools → Network → any{" "}
            <code>/api/v1/</code> request → Copy as cURL → the value after{" "}
            <code>-H &apos;Cookie: </code>). Includes{" "}
            <code>substack.sid</code> and other session cookies.
          </div>
          <div className="publish-targets-creds-reveal-row">
            <input
              className="settings-input mono"
              type={revealSubstack ? "text" : "password"}
              value={state.substackCookie}
              onChange={(e) =>
                onChange({ ...state, substackCookie: e.target.value })
              }
              placeholder={
                mode === "edit"
                  ? "(unchanged)"
                  : "substack.sid=s%3A...; ajs_anonymous_id=..."
              }
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              className="btn-ghost publish-targets-creds-reveal-btn"
              onClick={() => setRevealSubstack((v) => !v)}
              aria-label={
                revealSubstack ? "Hide session cookie" : "Show session cookie"
              }
            >
              {revealSubstack ? "Hide" : "Show"}
            </button>
          </div>
          {validation.substackCookieError && (
            <span className="settings-field-error">
              {validation.substackCookieError}
            </span>
          )}
        </div>
      )}
      {isTyped && type === "substack" && (
        <div className="settings-field">
          <span className="settings-label">
            Publication URL{" "}
            <span className="settings-label-hint">
              {mode === "edit"
                ? editHint
                : "Required — e.g. https://yourpub.substack.com"}
            </span>
          </span>
          <input
            className="settings-input"
            type="url"
            value={state.substackPublicationUrl}
            onChange={(e) =>
              onChange({ ...state, substackPublicationUrl: e.target.value })
            }
            placeholder={
              mode === "edit"
                ? "(unchanged)"
                : "https://yourpub.substack.com"
            }
            autoComplete="off"
            spellCheck={false}
          />
          {validation.substackPublicationUrlError && (
            <span className="settings-field-error">
              {validation.substackPublicationUrlError}
            </span>
          )}
        </div>
      )}

      {isTyped && type === "wordpress" && (
        <>
          <div className="publish-targets-creds-hint">
            WordPress application password (not your login password).
            Generate one at Users → Profile → Application Passwords in
            WordPress admin.
            {mode === "edit" ? ` ${editHint}` : ""}
          </div>
          <div className="settings-field">
            <span className="settings-label">Site URL</span>
            <input
              className="settings-input mono"
              type="url"
              value={state.wpSiteUrl}
              onChange={(e) =>
                onChange({ ...state, wpSiteUrl: e.target.value })
              }
              placeholder={
                mode === "edit" ? "(unchanged)" : "https://example.com"
              }
              autoComplete="off"
              spellCheck={false}
            />
            {validation.wpSiteUrlError && (
              <span className="settings-field-error">
                {validation.wpSiteUrlError}
              </span>
            )}
          </div>
          <div className="settings-field">
            <span className="settings-label">Username</span>
            <input
              className="settings-input"
              type="text"
              value={state.wpUsername}
              maxLength={WP_USERNAME_MAX}
              onChange={(e) =>
                onChange({ ...state, wpUsername: e.target.value })
              }
              placeholder={mode === "edit" ? "(unchanged)" : "editor"}
              autoComplete="off"
              spellCheck={false}
            />
            {validation.wpUsernameError && (
              <span className="settings-field-error">
                {validation.wpUsernameError}
              </span>
            )}
          </div>
          <div className="settings-field">
            <span className="settings-label">Application password</span>
            <input
              className="settings-input mono"
              type="password"
              value={state.wpAppPassword}
              onChange={(e) =>
                onChange({ ...state, wpAppPassword: e.target.value })
              }
              placeholder={
                mode === "edit" ? "(unchanged)" : "xxxx xxxx xxxx xxxx xxxx xxxx"
              }
              autoComplete="off"
              spellCheck={false}
            />
            {validation.wpAppPasswordError && (
              <span className="settings-field-error">
                {validation.wpAppPasswordError}
              </span>
            )}
          </div>
        </>
      )}

      {isTyped && type === "webhook" && (
        <>
          <div className="publish-targets-creds-hint">
            POST target for HMAC-signed JSON delivery. The receiver verifies
            the <code>X-CCM-Signature: sha256=&lt;hex&gt;</code> header
            (HMAC-SHA256 of the raw body) using the shared secret.
            {mode === "edit" ? ` ${editHint}` : ""}
          </div>
          <div className="settings-field">
            <span className="settings-label">Receiver URL</span>
            <input
              className="settings-input mono"
              type="url"
              value={state.webhookUrl}
              onChange={(e) =>
                onChange({ ...state, webhookUrl: e.target.value })
              }
              placeholder={
                mode === "edit"
                  ? "(unchanged)"
                  : "https://your-worker.workers.dev/publish"
              }
              autoComplete="off"
              spellCheck={false}
            />
            {validation.webhookUrlError && (
              <span className="settings-field-error">
                {validation.webhookUrlError}
              </span>
            )}
          </div>
          <div className="settings-field">
            <span className="settings-label">Shared secret</span>
            <input
              className="settings-input mono"
              type="password"
              value={state.webhookSecret}
              onChange={(e) =>
                onChange({ ...state, webhookSecret: e.target.value })
              }
              placeholder={mode === "edit" ? "(unchanged)" : "shared HMAC secret"}
              autoComplete="off"
              spellCheck={false}
            />
            {validation.webhookSecretError && (
              <span className="settings-field-error">
                {validation.webhookSecretError}
              </span>
            )}
          </div>
          <div className="settings-field">
            <span className="settings-label">
              Signature header{" "}
              <span className="settings-label-hint">
                Optional — defaults to X-CCM-Signature.
              </span>
            </span>
            <input
              className="settings-input mono"
              type="text"
              value={state.webhookHmacHeader}
              onChange={(e) =>
                onChange({ ...state, webhookHmacHeader: e.target.value })
              }
              placeholder="X-CCM-Signature"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </>
      )}

      {isTyped && type === "kit" && (
        <>
          <div className="publish-targets-creds-hint">
            Kit (kit.com) API key + sender identity. The API key is created in
            Kit under Settings → Advanced → API. Each piece publishes as a
            broadcast.
            {mode === "edit" ? ` ${editHint}` : ""}
          </div>
          <div className="settings-field">
            <span className="settings-label">API key</span>
            <input
              className="settings-input mono"
              type="password"
              value={state.kitApiKey}
              onChange={(e) =>
                onChange({ ...state, kitApiKey: e.target.value })
              }
              placeholder={mode === "edit" ? "(unchanged)" : "kit_..."}
              autoComplete="off"
              spellCheck={false}
            />
            {validation.kitApiKeyError && (
              <span className="settings-field-error">
                {validation.kitApiKeyError}
              </span>
            )}
          </div>
          <div className="settings-field">
            <span className="settings-label">From email</span>
            <input
              className="settings-input"
              type="email"
              value={state.kitFromEmail}
              onChange={(e) =>
                onChange({ ...state, kitFromEmail: e.target.value })
              }
              placeholder={mode === "edit" ? "(unchanged)" : "team@example.com"}
              autoComplete="off"
              spellCheck={false}
            />
            {validation.kitFromEmailError && (
              <span className="settings-field-error">
                {validation.kitFromEmailError}
              </span>
            )}
          </div>
          <div className="settings-field">
            <span className="settings-label">From name</span>
            <input
              className="settings-input"
              type="text"
              value={state.kitFromName}
              onChange={(e) =>
                onChange({ ...state, kitFromName: e.target.value })
              }
              placeholder={mode === "edit" ? "(unchanged)" : "B2X Marketing"}
              autoComplete="off"
              spellCheck={false}
            />
            {validation.kitFromNameError && (
              <span className="settings-field-error">
                {validation.kitFromNameError}
              </span>
            )}
          </div>
          {targetId != null ? (
            <>
              {configuredAudienceId && !state.kitAudienceId && (
                <div className="settings-label-hint" style={{ marginBottom: 6 }}>
                  Currently configured: <span className="mono">{configuredAudienceId}</span> — validate
                  below to see its name, or pick a different one.
                </div>
              )}
              <EspAudiencePicker
                targetId={targetId}
                audienceId={state.kitAudienceId}
                audienceName={state.kitAudienceName}
                onSelect={(id, name) =>
                  onChange({ ...state, kitAudienceId: id, kitAudienceName: name })
                }
              />
            </>
          ) : (
            <div className="settings-field">
              <span className="settings-label">
                Audience ID{" "}
                <span className="settings-label-hint">
                  Optional — save first, then edit this target to pick from a real list
                </span>
              </span>
              <input
                className="settings-input mono"
                type="text"
                value={state.kitAudienceId}
                onChange={(e) =>
                  onChange({ ...state, kitAudienceId: e.target.value, kitAudienceName: "" })
                }
                placeholder="e.g. 123456"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}
          <div className="settings-field">
            <span className="settings-label">
              Default subject prefix{" "}
              <span className="settings-label-hint">Optional</span>
            </span>
            <input
              className="settings-input"
              type="text"
              value={state.kitSubjectPrefix}
              onChange={(e) =>
                onChange({ ...state, kitSubjectPrefix: e.target.value })
              }
              placeholder="CMO Lab: "
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </>
      )}

      {isTyped && type === "mailchimp" && (
        <>
          <div className="publish-targets-creds-hint">
            Mailchimp API key + sender identity. The API key is created in
            Mailchimp under Account → Extras → API keys. Each piece publishes
            as a campaign to the selected audience.
            {mode === "edit" ? ` ${editHint}` : ""}
          </div>
          <div className="settings-field">
            <span className="settings-label">API key</span>
            <input
              className="settings-input mono"
              type="password"
              value={state.mailchimpApiKey}
              onChange={(e) =>
                onChange({ ...state, mailchimpApiKey: e.target.value })
              }
              placeholder={mode === "edit" ? "(unchanged)" : "abc123...-us21"}
              autoComplete="off"
              spellCheck={false}
            />
            {validation.mailchimpApiKeyError && (
              <span className="settings-field-error">
                {validation.mailchimpApiKeyError}
              </span>
            )}
          </div>
          <div className="settings-field">
            <span className="settings-label">From email</span>
            <input
              className="settings-input"
              type="email"
              value={state.mailchimpFromEmail}
              onChange={(e) =>
                onChange({ ...state, mailchimpFromEmail: e.target.value })
              }
              placeholder={mode === "edit" ? "(unchanged)" : "team@example.com"}
              autoComplete="off"
              spellCheck={false}
            />
            {validation.mailchimpFromEmailError && (
              <span className="settings-field-error">
                {validation.mailchimpFromEmailError}
              </span>
            )}
          </div>
          <div className="settings-field">
            <span className="settings-label">From name</span>
            <input
              className="settings-input"
              type="text"
              value={state.mailchimpFromName}
              onChange={(e) =>
                onChange({ ...state, mailchimpFromName: e.target.value })
              }
              placeholder={mode === "edit" ? "(unchanged)" : "B2X Marketing"}
              autoComplete="off"
              spellCheck={false}
            />
            {validation.mailchimpFromNameError && (
              <span className="settings-field-error">
                {validation.mailchimpFromNameError}
              </span>
            )}
          </div>
          {targetId != null ? (
            <>
              {configuredAudienceId && !state.mailchimpAudienceId && (
                <div className="settings-label-hint" style={{ marginBottom: 6 }}>
                  Currently configured: <span className="mono">{configuredAudienceId}</span> — validate
                  below to see its name, or pick a different one.
                </div>
              )}
              <EspAudiencePicker
                targetId={targetId}
                audienceId={state.mailchimpAudienceId}
                audienceName={state.mailchimpAudienceName}
                onSelect={(id, name) =>
                  onChange({ ...state, mailchimpAudienceId: id, mailchimpAudienceName: name })
                }
              />
            </>
          ) : (
            <div className="settings-field">
              <span className="settings-label">
                Audience ID{" "}
                <span className="settings-label-hint">
                  Optional — save first, then edit this target to pick from a real list
                </span>
              </span>
              <input
                className="settings-input mono"
                type="text"
                value={state.mailchimpAudienceId}
                onChange={(e) =>
                  onChange({
                    ...state,
                    mailchimpAudienceId: e.target.value,
                    mailchimpAudienceName: "",
                  })
                }
                placeholder="e.g. a1b2c3d4e5"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}
        </>
      )}

      {isTyped && FILE_OUTPUT_TYPES.has(type) && (
        <>
          <div className="publish-targets-creds-hint">
            File outputs upload to CCM's platform storage by default. Choose
            tenant R2 to deliver to your own Cloudflare R2 bucket with URLs
            you control.
            {mode === "edit" ? ` ${editHint}` : ""}
          </div>
          <div className="settings-field">
            <span className="settings-label">Upload target</span>
            <select
              className="settings-select"
              value={state.fileUploadTarget}
              onChange={(e) =>
                onChange({
                  ...state,
                  fileUploadTarget: e.target.value as
                    | "platform_r2"
                    | "tenant_r2",
                })
              }
            >
              <option value="platform_r2">Platform R2 (CCM-managed)</option>
              <option value="tenant_r2">Tenant R2 (bring your own bucket)</option>
            </select>
          </div>

          {state.fileUploadTarget === "tenant_r2" && (
            <>
              <div className="settings-field">
                <span className="settings-label">R2 account ID</span>
                <input
                  className="settings-input mono"
                  type="text"
                  value={state.fileTenantAccountId}
                  onChange={(e) =>
                    onChange({ ...state, fileTenantAccountId: e.target.value })
                  }
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <div className="settings-field">
                <span className="settings-label">Access key ID</span>
                <input
                  className="settings-input mono"
                  type="text"
                  value={state.fileTenantAccessKeyId}
                  onChange={(e) =>
                    onChange({
                      ...state,
                      fileTenantAccessKeyId: e.target.value,
                    })
                  }
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <div className="settings-field">
                <span className="settings-label">Secret access key</span>
                <input
                  className="settings-input mono"
                  type="password"
                  value={state.fileTenantSecretAccessKey}
                  onChange={(e) =>
                    onChange({
                      ...state,
                      fileTenantSecretAccessKey: e.target.value,
                    })
                  }
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <div className="settings-field">
                <span className="settings-label">Bucket</span>
                <input
                  className="settings-input mono"
                  type="text"
                  value={state.fileTenantBucket}
                  onChange={(e) =>
                    onChange({ ...state, fileTenantBucket: e.target.value })
                  }
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <div className="settings-field">
                <span className="settings-label">
                  Public URL base{" "}
                  <span className="settings-label-hint">Optional</span>
                </span>
                <input
                  className="settings-input mono"
                  type="url"
                  value={state.fileTenantPublicUrlBase}
                  onChange={(e) =>
                    onChange({
                      ...state,
                      fileTenantPublicUrlBase: e.target.value,
                    })
                  }
                  placeholder="https://files.example.com"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              {validation.fileTenantError && (
                <span className="settings-field-error">
                  {validation.fileTenantError}
                </span>
              )}
            </>
          )}

          <div className="settings-field">
            <span className="settings-label">
              Filename template{" "}
              <span className="settings-label-hint">
                Optional — supports {"{slug}"} and {"{id}"}
              </span>
            </span>
            <input
              className="settings-input mono"
              type="text"
              value={state.fileFilenameTemplate}
              onChange={(e) =>
                onChange({ ...state, fileFilenameTemplate: e.target.value })
              }
              placeholder="exports/{slug}-{id}"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          {type === "html_file" && (
            <>
              <div className="settings-field">
                <span className="settings-label">Image mode</span>
                <select
                  className="settings-select"
                  value={state.htmlFileImageMode}
                  onChange={(e) =>
                    onChange({
                      ...state,
                      htmlFileImageMode: e.target.value as
                        | "embedded"
                        | "separate",
                    })
                  }
                >
                  <option value="embedded">
                    Embedded (single self-contained .html)
                  </option>
                  <option value="separate">
                    Separate (.zip with index.html + images/)
                  </option>
                </select>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={state.htmlFileInlineCss}
                  onChange={(e) =>
                    onChange({ ...state, htmlFileInlineCss: e.target.checked })
                  }
                />
                <span className="settings-toggle-text">
                  <span className="settings-toggle-title">Inline CSS</span>
                  <span className="settings-toggle-sub">
                    Inject a minimal stylesheet so the file renders cleanly
                    standalone.
                  </span>
                </span>
              </label>
            </>
          )}
        </>
      )}

      {!isTyped && (
        <div className="settings-field">
          <span className="settings-label">
            Credentials JSON{" "}
            <span className="settings-label-hint">
              {mode === "edit"
                ? editHint
                : "Encrypted server-side. Never echoed back to the browser as plaintext after save."}
            </span>
          </span>
          <div className="settings-credentials-hint">
            {TARGET_TYPE_CREDENTIALS_HINT[type]}
          </div>
          <textarea
            className="voice-textarea voice-textarea-short mono"
            value={state.rawJson}
            onChange={(e) =>
              onChange({ ...state, rawJson: e.target.value })
            }
            placeholder={
              mode === "edit" ? "(unchanged)" : '{"key": "value"}'
            }
            spellCheck={false}
          />
          {validation.rawJsonError && (
            <span className="settings-field-error">
              {validation.rawJsonError}
            </span>
          )}
        </div>
      )}

      {isTyped && (
        <details
          className="publish-targets-creds-advanced"
          open={state.advancedOpen}
          onToggle={(e) =>
            onChange({
              ...state,
              advancedOpen: (e.target as HTMLDetailsElement).open,
            })
          }
        >
          <summary className="publish-targets-creds-advanced-summary">
            Advanced (paste raw JSON)
          </summary>
          <div className="publish-targets-creds-advanced-body">
            <div className="settings-credentials-hint">
              Operator escape hatch. If this textarea has valid JSON, it
              overrides the typed fields above. Leave blank to use the
              typed fields.
            </div>
            <textarea
              className="voice-textarea voice-textarea-short mono"
              value={state.rawJson}
              onChange={(e) =>
                onChange({ ...state, rawJson: e.target.value })
              }
              placeholder={
                type === "substack"
                  ? '{"session_cookie": "substack.sid=..."}'
                  : '{"username": "editor", "app_password": "xxxx ..."}'
              }
              spellCheck={false}
            />
            {validation.rawJsonError && (
              <span className="settings-field-error">
                {validation.rawJsonError}
              </span>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

// ---------- Top-level view ----------

export function PublishTargetsView() {
  const { data: workspaces, isLoading: wsLoading, error: wsError } =
    useApiQuery<Agencyclient[]>(["agencyclients"], "/agencyclients");

  const [selectedWsId, setSelectedWsId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(PT_WS_KEY);
    return raw ? Number(raw) : null;
  });

  useEffect(() => {
    if (selectedWsId != null) {
      try {
        window.sessionStorage.setItem(PT_WS_KEY, String(selectedWsId));
      } catch {
        /* sessionStorage unavailable — tolerate */
      }
    }
  }, [selectedWsId]);

  // Default to first workspace once the list loads.
  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      const stillValid = workspaces.some((w) => w.id === selectedWsId);
      if (!stillValid) setSelectedWsId(workspaces[0].id);
    }
  }, [workspaces, selectedWsId]);

  if (wsLoading) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <div className="state-loading">Loading publish targets…</div>
        </div>
      </main>
    );
  }

  if (wsError) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <div className="state-error">
            {wsError instanceof ApiError && wsError.status === 403
              ? "You don't have permission to view publish targets."
              : (wsError.message ?? "Could not load workspaces.")}
          </div>
        </div>
      </main>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <Header />
          <div className="settings-empty">
            <h2>No workspaces yet</h2>
            <p>
              Create a workspace under Settings → Workspaces, then add a
              publication, before configuring publish targets.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <PublishTargetsBody
      workspaces={workspaces}
      selectedWsId={selectedWsId}
      onSelectWs={setSelectedWsId}
    />
  );
}

function Header() {
  return (
    <>
      <nav className="editor-breadcrumb" aria-label="Breadcrumb">
        <span>Settings</span>
        <span className="editor-breadcrumb-sep">/</span>
        <span>Publish targets</span>
      </nav>
      <SectionHeader
        eyebrow="Settings · Publish Targets"
        title="Publish targets"
        description="Per-publication distribution destinations. Credentials are encrypted at rest with AES-256-GCM and never echoed back to the browser after save."
      />
    </>
  );
}

interface BodyProps {
  workspaces: Agencyclient[];
  selectedWsId: number | null;
  onSelectWs: (id: number) => void;
}

function PublishTargetsBody({
  workspaces,
  selectedWsId,
  onSelectWs,
}: BodyProps) {
  const qc = useQueryClient();
  const activeWs = useMemo(
    () => workspaces.find((w) => w.id === selectedWsId) ?? workspaces[0],
    [workspaces, selectedWsId]
  );
  const activeWsId = activeWs.id;

  // S-199 — sync the JWT agencyclient_id claim to the displayed workspace on
  // load so the publications + publish-targets reads (RLS scope) and mutations
  // target it. Without this the panel shows empty until the user switches
  // workspace and back.
  useEnsureActiveWorkspace(activeWsId);

  const { activate, isActivating, error: switchError, clearError } =
    useActiveWorkspace();

  // Publications in the active workspace — drives the publication selector.
  const { data: pubsResp, isLoading: pubsLoading, error: pubsError } =
    useApiQuery<PublicationsResponse>(
      ["publications", activeWsId],
      `/publications?agencyclient_id=${activeWsId}`
    );

  const publications = pubsResp?.data ?? [];

  const [selectedPubId, setSelectedPubId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(PT_PUB_KEY);
    return raw ? Number(raw) : null;
  });

  useEffect(() => {
    if (selectedPubId != null) {
      try {
        window.sessionStorage.setItem(PT_PUB_KEY, String(selectedPubId));
      } catch {
        /* tolerate */
      }
    }
  }, [selectedPubId]);

  // Default to first publication once the list loads, and reset if the
  // current selection isn't in the new workspace's publications.
  useEffect(() => {
    if (publications.length === 0) return;
    const stillValid = publications.some((p) => p.id === selectedPubId);
    if (!stillValid) setSelectedPubId(publications[0].id);
  }, [publications, selectedPubId]);

  // Publish targets — list endpoint filters by agencyclient_id only
  // (not publication_id), so we fetch the workspace's full set and
  // filter client-side. Realistic n ≤ ~10 targets/workspace.
  const {
    data: targetsResp,
    isLoading: targetsLoading,
    error: targetsError,
  } = useApiQuery<PublishTargetsResponse>(
    ["publish-targets", activeWsId],
    `/publish-targets?agencyclient_id=${activeWsId}`
  );

  const scopedTargets = useMemo(() => {
    const rows = targetsResp?.data ?? [];
    if (selectedPubId == null) return [];
    return rows.filter((t) => pubIdOf(t) === selectedPubId);
  }, [targetsResp, selectedPubId]);

  // S-374 — the dependency runs publication → target, so a publication with
  // no target is the thing worth seeing, and it is invisible while you can
  // only look at one publication at a time. The list endpoint already
  // returns the whole workspace's targets, so this costs no extra request.
  const publicationsWithoutTarget = useMemo(() => {
    const rows = targetsResp?.data ?? [];
    if (rows.length === 0 && targetsLoading) return [];
    const withTarget = new Set(rows.map((t) => pubIdOf(t)).filter((id) => id != null));
    return publications.filter((p) => !withTarget.has(p.id));
  }, [targetsResp, publications, targetsLoading]);

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<PublishTarget | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PublishTarget | null>(
    null
  );
  // Phase 5 UX polish parity (2026-05-25): mirror the pending-select
  // pattern from PublicationsView so the workspace selector reflects
  // intent during activate() without dropping the publications list.
  const [pendingWsId, setPendingWsId] = useState<number | null>(null);

  function invalidateTargets() {
    void qc.invalidateQueries({ queryKey: ["publish-targets", activeWsId] });
  }

  async function handleWorkspaceChange(nextId: number) {
    if (nextId === activeWsId) return;
    clearError();
    setPendingWsId(nextId);
    try {
      await activate(nextId);
      onSelectWs(nextId);
      // Reset publication selection so the empty/list state recomputes
      // against the new workspace's publications.
      setSelectedPubId(null);
    } catch {
      /* useActiveWorkspace surfaces error via switchError */
    } finally {
      setPendingWsId(null);
    }
  }

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <Header />

        <div className="team-toolbar">
          <span className="team-toolbar-label">Workspace</span>
          <select
            className="settings-select"
            value={pendingWsId ?? activeWsId}
            disabled={isActivating}
            onChange={(e) => {
              void handleWorkspaceChange(Number(e.target.value));
            }}
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.slug})
              </option>
            ))}
          </select>
          {isActivating && (
            <span className="team-toolbar-hint">Switching workspace…</span>
          )}
          {switchError && !isActivating && (
            <span className="settings-field-error">
              {switchError.status === 403
                ? "You don't have access to that workspace."
                : (switchError.message ?? "Could not switch workspace.")}
            </span>
          )}
        </div>

        {!pubsLoading && !pubsError && publications.length > 0 && (
          <div className="team-toolbar">
            <span className="team-toolbar-label">Publication</span>
            <select
              className="settings-select"
              value={selectedPubId ?? publications[0].id}
              onChange={(e) => setSelectedPubId(Number(e.target.value))}
            >
              {publications.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.slug})
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowCreate(true)}
              disabled={isActivating}
              style={{ marginLeft: "auto" }}
            >
              + New target
            </button>
          </div>
        )}

        {pubsLoading && (
          <div className="state-loading">Loading publications…</div>
        )}

        {pubsError && !pubsLoading && (
          <div className="state-error">
            {pubsError.message ?? "Could not load publications."}
          </div>
        )}

        {!pubsLoading && !pubsError && publications.length === 0 && (
          <div className="settings-empty">
            <h2>No publications in this workspace</h2>
            <p>
              Publish targets attach to publications. Create a publication
              first.
            </p>
            <Link to="/settings/publications" className="btn-primary">
              Go to publications
            </Link>
          </div>
        )}

        {!pubsLoading &&
          !pubsError &&
          publications.length > 0 &&
          selectedPubId == null && (
            <div className="settings-empty">
              <p>Select a publication to manage its publish targets.</p>
            </div>
          )}

        {!pubsLoading && !targetsLoading && publicationsWithoutTarget.length > 0 && (
          <div className="settings-warn-block">
            <strong>
              {publicationsWithoutTarget.length === 1
                ? "One publication has no publish target"
                : `${publicationsWithoutTarget.length} publications have no publish target`}
            </strong>
            <p>
              Their pieces can be written, reviewed and approved, and then have
              nowhere to go:{" "}
              {publicationsWithoutTarget.map((p) => p.name).join(", ")}.
            </p>
          </div>
        )}

        {selectedPubId != null && targetsLoading && (
          <div className="state-loading">Loading publish targets…</div>
        )}

        {selectedPubId != null && targetsError && !targetsLoading && (
          <div className="state-error">
            {targetsError instanceof ApiError && targetsError.status === 403
              ? "You don't have permission to view publish targets."
              : (targetsError.message ?? "Could not load publish targets.")}
          </div>
        )}

        {selectedPubId != null &&
          !targetsLoading &&
          !targetsError &&
          scopedTargets.length === 0 && (
            <div className="settings-empty">
              <h2>No publish targets yet</h2>
              <p>
                Without one this publication produces <strong>a piece you can
                approve but never send</strong>. Add a Substack, WordPress or
                other destination so its pieces can ship.
              </p>
              <button
                type="button"
                className="btn-primary"
                onClick={() => setShowCreate(true)}
              >
                + New target
              </button>
            </div>
          )}

        {selectedPubId != null &&
          !targetsLoading &&
          !targetsError &&
          scopedTargets.length > 0 && (
            <PublishTargetsTable
              rows={scopedTargets}
              onEdit={setEditing}
              onDelete={setConfirmDelete}
            />
          )}
      </div>

      {showCreate && selectedPubId != null && (
        <CreateTargetModal
          agencyclientId={activeWsId}
          publicationId={selectedPubId}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            invalidateTargets();
            setShowCreate(false);
          }}
        />
      )}

      {editing && (
        <EditTargetDrawer
          target={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            invalidateTargets();
            setEditing(null);
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          target={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onDeleted={() => {
            invalidateTargets();
            setConfirmDelete(null);
          }}
        />
      )}
    </main>
  );
}

// ---------- Table ----------

interface TableProps {
  rows: PublishTarget[];
  onEdit: (t: PublishTarget) => void;
  onDelete: (t: PublishTarget) => void;
}

function PublishTargetsTable({ rows, onEdit, onDelete }: TableProps) {
  return (
    <table className="workspaces-table">
      <thead>
        <tr>
          <th>Type</th>
          <th>Name</th>
          <th>Slug</th>
          <th>Status</th>
          <th>Credentials</th>
          <th>Created</th>
          <th className="ws-actions-col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((t) => (
          <tr
            key={t.id}
            className="workspaces-row"
            onClick={() => onEdit(t)}
          >
            <td>
              <span className="pill pill-info">
                {TARGET_TYPE_LABEL[t.type] ?? t.type}
              </span>
            </td>
            <td className="ws-name-cell">
              <span className="ws-name">{t.name}</span>
              {siteUrlOf(t) && (
                <span className="ws-sub-meta">{siteUrlOf(t)}</span>
              )}
            </td>
            <td>
              <code className="mono ws-slug">{t.slug}</code>
            </td>
            <td>
              <span
                className={`pill ${t.enabled ? "pill-ok" : "pill-muted"}`}
              >
                {t.enabled ? "Enabled" : "Disabled"}
              </span>
            </td>
            <td>
              {t.credentials_ciphertext ? (
                <code className="mono ws-slug">***</code>
              ) : (
                <span className="pill pill-warn">None</span>
              )}
            </td>
            <td className="ws-date">{fmtDate(createdAtOf(t))}</td>
            <td className="ws-actions" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => onEdit(t)}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn-ghost btn-danger"
                onClick={() => onDelete(t)}
              >
                Delete
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------- Create modal ----------

interface CreateProps {
  agencyclientId: number;
  publicationId: number;
  onClose: () => void;
  onCreated: () => void;
}

interface CreateBody {
  agencyclient_id: number;
  publication_id: number;
  type: TargetType;
  name: string;
  slug: string;
  site_url?: string;
  credentials?: string;
  enabled?: boolean;
}

function CreateTargetModal({
  agencyclientId,
  publicationId,
  onClose,
  onCreated,
}: CreateProps) {
  const api = useApi();
  const [type, setType] = useState<TargetType>("substack");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [siteUrl, setSiteUrl] = useState("");
  // S-078: typed credentials state replaces the raw textarea.
  const [credState, setCredState] = useState<CredentialsState>(
    emptyCredentialsState
  );
  const [enabled, setEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!slugDirty) setSlug(slugify(name));
  }, [name, slugDirty]);

  // Reset typed fields when target type switches so a half-typed
  // substack cookie doesn't bleed into a wordpress submit.
  useEffect(() => {
    setCredState(emptyCredentialsState());
  }, [type]);

  const credValidation = useMemo(
    () => validateCredentials(credState, type, "create"),
    [credState, type]
  );

  const slugError = slug.length > 0 && !isValidSlug(slug);
  const nameError = name.trim().length === 0 || name.trim().length > 120;
  const collisionError =
    serverError instanceof ApiError && serverError.status === 409;
  const validationError =
    serverError instanceof ApiError && serverError.status === 422;
  const urlError =
    siteUrl.trim().length > 0 && !isProbablyUrl(siteUrl.trim());

  const canSubmit =
    !submitting &&
    !nameError &&
    !slugError &&
    !urlError &&
    !credValidation.blockSubmit &&
    slug.length > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const body: CreateBody = {
        agencyclient_id: agencyclientId,
        publication_id: publicationId,
        type,
        name: name.trim(),
        slug,
        enabled,
      };
      if (siteUrl.trim().length > 0) body.site_url = siteUrl.trim();
      if (credValidation.toJson !== null) {
        body.credentials = credValidation.toJson;
      }
      await api<PublishTarget>("/publish-targets", {
        method: "POST",
        body: JSON.stringify(body),
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err);
      } else {
        setServerError(
          new ApiError(0, String(err), "Could not create publish target")
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell onClose={onClose} eyebrow="Publish targets" title="New publish target">
      <form className="settings-form" onSubmit={submit}>
        <label className="settings-field">
          <span className="settings-label">Type</span>
          <select
            className="settings-select"
            value={type}
            onChange={(e) => setType(e.target.value as TargetType)}
          >
            {TARGET_TYPES.map((tt) => (
              <option key={tt} value={tt}>
                {TARGET_TYPE_LABEL[tt]}
              </option>
            ))}
          </select>
        </label>

        <label className="settings-field">
          <span className="settings-label">Name</span>
          <input
            className="settings-input"
            type="text"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="e.g. CMO Lab Newsletter — Substack"
          />
          {nameError && name.length > 0 && (
            <span className="settings-field-error">
              Name must be 1–120 characters.
            </span>
          )}
        </label>

        <label className="settings-field">
          <span className="settings-label">
            Slug{" "}
            <span className="settings-label-hint">URL-safe identifier</span>
          </span>
          <input
            className="settings-input mono"
            type="text"
            value={slug}
            maxLength={80}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugDirty(true);
            }}
            placeholder="cmo-lab-substack"
          />
          {slugError && (
            <span className="settings-field-error">
              Kebab-case only: lowercase letters, digits, and hyphens.
            </span>
          )}
          {collisionError && (
            <span className="settings-field-error">
              That slug is already taken for this publication.
            </span>
          )}
        </label>

        <label className="settings-field">
          <span className="settings-label">
            Site URL{" "}
            <span className="settings-label-hint">Optional</span>
          </span>
          <input
            className="settings-input"
            type="url"
            value={siteUrl}
            maxLength={2048}
            onChange={(e) => setSiteUrl(e.target.value)}
            placeholder="https://thecmolab.substack.com"
          />
          {urlError && (
            <span className="settings-field-error">
              Site URL must be a valid http(s) URL.
            </span>
          )}
        </label>

        <CredentialsSection
          type={type}
          state={credState}
          onChange={setCredState}
          validation={credValidation}
          mode="create"
        />

        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span className="settings-toggle-text">
            <span className="settings-toggle-title">Enabled</span>
            <span className="settings-toggle-sub">
              When off, the publisher skips this target during pipeline
              runs.
            </span>
          </span>
        </label>

        {validationError && !collisionError && (
          <div className="settings-form-error">
            {serverError?.body || "The server rejected the request."}
          </div>
        )}
        {serverError &&
          !collisionError &&
          !validationError &&
          serverError.status !== 409 && (
            <div className="settings-form-error">{serverError.message}</div>
          )}

        <div className="settings-form-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
          >
            {submitting ? "Creating…" : "Create publish target"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---------- Edit drawer ----------

interface EditProps {
  target: PublishTarget;
  onClose: () => void;
  onSaved: () => void;
}

interface UpdateBody {
  type?: TargetType;
  name?: string;
  slug?: string;
  site_url?: string;
  credentials?: string;
  // BUG-037 — merge-patch a handful of credential keys server-side instead
  // of replacing the whole blob. Mutually exclusive with `credentials`.
  credentials_patch?: Record<string, string>;
  enabled?: boolean;
}

function EditTargetDrawer({ target, onClose, onSaved }: EditProps) {
  const [type, setType] = useState<TargetType>(target.type);
  const [name, setName] = useState(target.name);
  const [slug, setSlug] = useState(target.slug);
  const [siteUrl, setSiteUrl] = useState(siteUrlOf(target) ?? "");
  // S-078: typed credentials state starts blank in edit mode; blank →
  // keep existing ciphertext (we never echo plaintext back, so there's
  // no value to pre-fill).
  const [credState, setCredState] = useState<CredentialsState>(
    emptyCredentialsState
  );
  const [enabled, setEnabled] = useState(target.enabled);
  const [recentlySaved, setRecentlySaved] = useState(false);

  // Reset typed fields when target type switches mid-edit so the wrong
  // field shape can't bleed into the submit body.
  useEffect(() => {
    setCredState(emptyCredentialsState());
  }, [type]);

  const credValidation = useMemo(
    () => validateCredentials(credState, type, "edit"),
    [credState, type]
  );

  const mutation = useApiMutation<PublishTarget, UpdateBody>(
    `/publish-targets/${target.id}`,
    "PATCH",
    {
      onSuccess: () => {
        setRecentlySaved(true);
        // Surface confirmation, then close after a short delay so the
        // user actually sees the "Saved" pill (mirror Voice/Brand
        // editors' polish pattern).
        window.setTimeout(() => onSaved(), 600);
      },
    }
  );

  // Auto-clear the inline badge after a short delay (parity with the
  // editor pages even though this is a drawer that closes on save).
  useEffect(() => {
    if (!recentlySaved) return;
    const t = window.setTimeout(() => setRecentlySaved(false), 2500);
    return () => window.clearTimeout(t);
  }, [recentlySaved]);

  const initialSiteUrl = siteUrlOf(target) ?? "";

  // Credentials are dirty when validateCredentials() yields a non-null
  // JSON payload (typed fields touched, or raw JSON entered), or a
  // BUG-037 audience-only patch (picker used, nothing else touched).
  const credsDirty =
    credValidation.toJson !== null || credValidation.credentialsPatch != null;

  const dirty = useMemo(() => {
    return (
      type !== target.type ||
      name.trim() !== target.name ||
      slug !== target.slug ||
      siteUrl !== initialSiteUrl ||
      enabled !== target.enabled ||
      credsDirty
    );
  }, [type, name, slug, siteUrl, enabled, credsDirty, target, initialSiteUrl]);

  const slugError = slug.length > 0 && !isValidSlug(slug);
  const nameError = name.trim().length === 0 || name.trim().length > 120;
  const urlError =
    siteUrl.trim().length > 0 && !isProbablyUrl(siteUrl.trim());
  const collisionError =
    mutation.error instanceof ApiError && mutation.error.status === 409;
  const canSubmit =
    !mutation.isPending &&
    dirty &&
    !nameError &&
    !slugError &&
    !urlError &&
    !credValidation.blockSubmit;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const body: UpdateBody = {};
    if (type !== target.type) body.type = type;
    if (name.trim() !== target.name) body.name = name.trim();
    if (slug !== target.slug) body.slug = slug;
    if (siteUrl !== initialSiteUrl) body.site_url = siteUrl.trim();
    if (enabled !== target.enabled) body.enabled = enabled;
    if (credValidation.toJson !== null) {
      body.credentials = credValidation.toJson;
    } else if (credValidation.credentialsPatch != null) {
      body.credentials_patch = credValidation.credentialsPatch;
    }
    mutation.mutate(body);
  }

  return (
    <DrawerShell onClose={onClose} eyebrow="Publish targets" title="Edit publish target">
      <form className="settings-form" onSubmit={submit}>
        <label className="settings-field">
          <span className="settings-label">Type</span>
          <select
            className="settings-select"
            value={type}
            onChange={(e) => setType(e.target.value as TargetType)}
          >
            {TARGET_TYPES.map((tt) => (
              <option key={tt} value={tt}>
                {TARGET_TYPE_LABEL[tt]}
              </option>
            ))}
          </select>
        </label>

        <label className="settings-field">
          <span className="settings-label">Name</span>
          <input
            className="settings-input"
            type="text"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          {nameError && (
            <span className="settings-field-error">
              Name must be 1–120 characters.
            </span>
          )}
        </label>

        <label className="settings-field">
          <span className="settings-label">Slug</span>
          <input
            className="settings-input mono"
            type="text"
            value={slug}
            maxLength={80}
            onChange={(e) => setSlug(e.target.value)}
          />
          {slugError && (
            <span className="settings-field-error">
              Kebab-case only: lowercase letters, digits, and hyphens.
            </span>
          )}
          {collisionError && (
            <span className="settings-field-error">
              That slug is already taken for this publication.
            </span>
          )}
        </label>

        <label className="settings-field">
          <span className="settings-label">Site URL</span>
          <input
            className="settings-input"
            type="url"
            value={siteUrl}
            maxLength={2048}
            onChange={(e) => setSiteUrl(e.target.value)}
            placeholder="https://"
          />
          {urlError && (
            <span className="settings-field-error">
              Site URL must be a valid http(s) URL.
            </span>
          )}
        </label>

        <CredentialsSection
          type={type}
          state={credState}
          onChange={setCredState}
          validation={credValidation}
          mode="edit"
          targetId={target.id}
          configuredAudienceId={target.configured_audience_id}
        />

        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span className="settings-toggle-text">
            <span className="settings-toggle-title">Enabled</span>
            <span className="settings-toggle-sub">
              When off, the publisher skips this target during pipeline
              runs.
            </span>
          </span>
        </label>

        {mutation.error && !collisionError && (
          <div className="settings-form-error">
            {mutation.error.body || mutation.error.message}
          </div>
        )}

        <div className="settings-form-actions">
          {recentlySaved && (
            <span className="editor-saved-badge" role="status">
              Saved
            </span>
          )}
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
          >
            {mutation.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </DrawerShell>
  );
}

// ---------- Delete confirm ----------

interface ConfirmDeleteProps {
  target: PublishTarget;
  onCancel: () => void;
  onDeleted: () => void;
}

function ConfirmDeleteModal({
  target,
  onCancel,
  onDeleted,
}: ConfirmDeleteProps) {
  const mutation = useApiMutation<void, void>(
    `/publish-targets/${target.id}`,
    "DELETE",
    {
      onSuccess: () => onDeleted(),
    }
  );

  return (
    <ModalShell onClose={onCancel} eyebrow="Publish targets · danger" title="Delete publish target?">
      <p className="settings-confirm-copy">
        Remove <strong>{target.name}</strong>? This deletes the publish
        target. Historical publish results that reference this target are
        FK-protected (RESTRICT) and will block delete; in that case,
        disable the target instead.
      </p>
      {mutation.error && (
        <div className="settings-form-error">{mutation.error.message}</div>
      )}
      <div className="settings-form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary btn-danger"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Deleting…" : "Delete target"}
        </button>
      </div>
    </ModalShell>
  );
}

// ---------- URL validator ----------

function isProbablyUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

// NOTE: the local ModalShell / DrawerShell that used to live here were removed
// in the option-3 consolidation — both now come from
// @/components/SettingsModalShell (imported at the top of this file). The shared
// shell renders the canonical anatomy (eyebrow + 24px serif title, bordered
// close, blurred overlay) and handles Escape/backdrop close.
